import { useState, useMemo } from 'react';
import {
  ChevronRight,
  FileText,
  Search,
  X,
  ArrowUpDown,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { GradeBadge, type LetterGrade } from './GradeBadge';
import { SeverityDot } from './SeverityBadge';

export interface PageInfo {
  url: string;
  title?: string;
  score: number;
  grade: LetterGrade | string;
  issueCount: number;
  issues?: {
    critical: number;
    serious: number;
    moderate: number;
    minor: number;
  };
}

interface PageNavigatorProps {
  pages: PageInfo[];
  selectedPageUrl?: string | null;
  onPageSelect: (pageUrl: string | null) => void;
  showSearch?: boolean;
  showSort?: boolean;
  maxHeight?: string;
}

type SortOption = 'score-asc' | 'score-desc' | 'issues-asc' | 'issues-desc' | 'url';

export function PageNavigator({
  pages,
  selectedPageUrl,
  onPageSelect,
  showSearch = true,
  showSort = true,
  maxHeight = '400px',
}: PageNavigatorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('issues-desc');

  const filteredAndSortedPages = useMemo(() => {
    let result = [...pages];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (page) =>
          page.url.toLowerCase().includes(query) ||
          page.title?.toLowerCase().includes(query)
      );
    }

    // Sort
    switch (sortBy) {
      case 'score-asc':
        result.sort((a, b) => a.score - b.score);
        break;
      case 'score-desc':
        result.sort((a, b) => b.score - a.score);
        break;
      case 'issues-asc':
        result.sort((a, b) => a.issueCount - b.issueCount);
        break;
      case 'issues-desc':
        result.sort((a, b) => b.issueCount - a.issueCount);
        break;
      case 'url':
        result.sort((a, b) => a.url.localeCompare(b.url));
        break;
    }

    return result;
  }, [pages, searchQuery, sortBy]);

  const truncateUrl = (url: string, maxLength = 50): string => {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      if (path.length <= maxLength) return path || '/';
      return '...' + path.slice(-maxLength);
    } catch {
      if (url.length <= maxLength) return url;
      return '...' + url.slice(-maxLength);
    }
  };

  return (
    <div className="border border-gray-light rounded-lg overflow-hidden bg-white">
      {/* Header with search and sort */}
      <div className="p-3 bg-gray-50 border-b border-gray-light space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-body font-semibold flex items-center gap-2">
            <FileText size={18} className="text-coral" />
            Pages ({pages.length})
          </h3>
          {selectedPageUrl && (
            <button
              onClick={() => onPageSelect(null)}
              className="text-body-sm text-coral hover:text-coral/80 flex items-center gap-1"
            >
              <X size={14} />
              Clear selection
            </button>
          )}
        </div>

        {/* Search */}
        {showSearch && (
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search pages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-body-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Sort options */}
        {showSort && (
          <div className="flex items-center gap-2">
            <ArrowUpDown size={14} className="text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="text-body-sm border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-coral/30"
            >
              <option value="issues-desc">Most issues first</option>
              <option value="issues-asc">Least issues first</option>
              <option value="score-asc">Lowest score first</option>
              <option value="score-desc">Highest score first</option>
              <option value="url">URL (A-Z)</option>
            </select>
          </div>
        )}
      </div>

      {/* Pages list */}
      <div className="overflow-y-auto" style={{ maxHeight }}>
        {filteredAndSortedPages.length === 0 ? (
          <div className="p-6 text-center text-gray-medium">
            {searchQuery ? (
              <>
                <Search size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-body-sm">No pages match your search</p>
              </>
            ) : (
              <>
                <FileText size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-body-sm">No pages available</p>
              </>
            )}
          </div>
        ) : (
          <ul role="listbox" aria-label="Select a page to view details">
            {filteredAndSortedPages.map((page) => (
              <PageListItem
                key={page.url}
                page={page}
                isSelected={selectedPageUrl === page.url}
                onSelect={() => onPageSelect(page.url)}
                truncateUrl={truncateUrl}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface PageListItemProps {
  page: PageInfo;
  isSelected: boolean;
  onSelect: () => void;
  truncateUrl: (url: string) => string;
}

function PageListItem({ page, isSelected, onSelect, truncateUrl }: PageListItemProps) {
  return (
    <li>
      <button
        onClick={onSelect}
        className={`w-full flex items-center gap-3 p-3 text-left border-b border-gray-light last:border-b-0 transition-colors
          ${isSelected ? 'bg-coral/5 border-l-2 border-l-coral' : 'hover:bg-gray-50'}`}
        role="option"
        aria-selected={isSelected}
      >
        {/* Grade badge */}
        <GradeBadge grade={page.grade as LetterGrade} size="sm" />

        {/* Page info */}
        <div className="flex-1 min-w-0">
          <p className="text-body-sm font-medium text-dark-text truncate" title={page.url}>
            {truncateUrl(page.url)}
          </p>
          {page.title && (
            <p className="text-caption text-gray-medium truncate">{page.title}</p>
          )}

          {/* Issue counts */}
          {page.issues && (
            <div className="flex items-center gap-3 mt-1">
              {page.issues.critical > 0 && (
                <span className="flex items-center gap-1 text-caption">
                  <SeverityDot severity="critical" size="sm" />
                  {page.issues.critical}
                </span>
              )}
              {page.issues.serious > 0 && (
                <span className="flex items-center gap-1 text-caption">
                  <SeverityDot severity="serious" size="sm" />
                  {page.issues.serious}
                </span>
              )}
              {page.issues.moderate > 0 && (
                <span className="flex items-center gap-1 text-caption">
                  <SeverityDot severity="moderate" size="sm" />
                  {page.issues.moderate}
                </span>
              )}
              {page.issues.minor > 0 && (
                <span className="flex items-center gap-1 text-caption">
                  <SeverityDot severity="minor" size="sm" />
                  {page.issues.minor}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Issue count / status indicator */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {page.issueCount > 0 ? (
            <span className="flex items-center gap-1 text-body-sm text-gray-medium">
              <AlertTriangle size={14} className="text-status-warning" />
              {page.issueCount}
            </span>
          ) : (
            <CheckCircle size={16} className="text-status-success" />
          )}
          <ChevronRight size={16} className="text-gray-400" />
        </div>
      </button>
    </li>
  );
}

interface PageNavigatorSkeletonProps {
  itemCount?: number;
}

export function PageNavigatorSkeleton({ itemCount = 5 }: PageNavigatorSkeletonProps) {
  return (
    <div className="border border-gray-light rounded-lg overflow-hidden animate-pulse">
      <div className="p-3 bg-gray-50 border-b border-gray-light">
        <div className="h-5 w-32 bg-gray-light rounded mb-3" />
        <div className="h-9 w-full bg-gray-light rounded" />
      </div>
      <div>
        {Array.from({ length: itemCount }).map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-3 p-3 border-b border-gray-light last:border-b-0"
          >
            <div className="w-6 h-6 bg-gray-light rounded-full" />
            <div className="flex-1">
              <div className="h-4 w-3/4 bg-gray-light rounded mb-1" />
              <div className="h-3 w-1/2 bg-gray-light rounded" />
            </div>
            <div className="h-4 w-8 bg-gray-light rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}
